import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
// This page is the destination Emergent OAuth redirects back to. The URL will
// look like `/auth/callback#session_id=<id>`. We exchange the session_id ONCE
// (StrictMode-safe via useRef) and then navigate the user into the app.
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      toast.error("No session id returned from Google login");
      nav("/auth", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const referral_code = localStorage.getItem("tb_ref_code") || undefined;
        await api.post("/auth/google/exchange", { session_id: sessionId, referral_code });
        if (referral_code) localStorage.removeItem("tb_ref_code");
        await refresh();
        toast.success("Welcome to TravelBio!");
        // Recover the pre-auth intended URL persisted by <Protected/>
        // (or loginWithGoogle when the user began the OAuth flow from
        // `/auth?next=…`). Sanitize before navigating so a crafted
        // sessionStorage value can't cause an open redirect.
        let nextUrl = "";
        try {
          nextUrl = window.sessionStorage.getItem("tb_post_auth_next") || "";
          window.sessionStorage.removeItem("tb_post_auth_next");
        } catch { /* noop */ }
        const isSafe = nextUrl
          && nextUrl.startsWith("/")
          && !nextUrl.startsWith("//")
          && nextUrl !== "/auth"
          && !nextUrl.startsWith("/auth?")
          && !nextUrl.startsWith("/auth/");
        const dest = isSafe ? nextUrl : "/explore";
        // Wipe the fragment before the redirect so `#session_id=…`
        // doesn't linger in the address bar.
        window.history.replaceState({}, "", dest);
        nav(dest, { replace: true });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed");
        nav("/auth", { replace: true });
      }
    })();
  }, [nav, refresh]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep">
      <div className="text-center">
        <BrandLogo size="lg" theme="navy" />
        <div className="font-hand text-tb-gold/80 text-xl italic mt-4">~ opening your journal ~</div>
      </div>
    </div>
  );
}
